import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import { motion, AnimatePresence } from 'framer-motion';

import UserDropdown from './common/UserDropdown';
import NotificationPanel from './NotificationPanel';
import { toggleSidebar } from '../store/sidebarSlice';
import { useThemeMode } from '../theme';
import { activeSection, visibleSections } from '../navigation';

const AppHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useThemeMode();
  const { permissions = [] } = useSelector((state) => state.auth);

  // One tree, filtered to what this role may open, so the header cannot offer a
  // route that lands on Unauthorized.
  const sections = React.useMemo(() => visibleSections(permissions), [permissions]);
  const current = activeSection(sections, location.pathname);

  // A section opens at its first permitted item; Dashboard has none and carries
  // its own path.
  const open = (section) => navigate(section.items[0]?.path || section.path);

  // The drawer holds something only when the active section has items, or on
  // mobile where it carries the whole tree.
  const hasDrawer = isMobile || Boolean(current?.items.length);

  return (
    <AppBar position="sticky" elevation={0} sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Toolbar sx={{ gap: { xs: 0.5, sm: 1 }, minHeight: { xs: 56, sm: 64 }, overflow: 'hidden', px: { xs: 1, sm: 2 } }}>
        {hasDrawer && (
          <IconButton
            color="inherit"
            onClick={() => dispatch(toggleSidebar())}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              background: alpha(theme.palette.text.primary, 0.08),
              borderRadius: 2,
              '&:hover': { background: alpha(theme.palette.text.primary, 0.15) },
              flexShrink: 0,
            }}
            aria-label={isMobile ? 'Open navigation' : `Toggle ${current?.label} menu`}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box
          component="img"
          src="/assets/logo.png"
          alt="AIVIZ PMS"
          sx={{
            height: { xs: 26, sm: 38 },
            objectFit: 'contain',
            cursor: 'pointer',
            filter: theme.palette.mode === 'dark' ? 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))' : 'none',
            flexShrink: 0,
          }}
          onClick={() => navigate('/dashboard')}
        />

        {!isMobile && (
          <Box
            component="nav"
            aria-label="Main"
            sx={{ display: 'flex', gap: 0.5, ml: 3, minWidth: 0 }}
          >
            {sections.map((section) => {
              const selected = current?.id === section.id;
              return (
                <Button
                  key={section.id}
                  color="inherit"
                  onClick={() => open(section)}
                  aria-current={selected ? 'page' : undefined}
                  sx={{
                    position: 'relative',
                    flexShrink: 0,
                    fontWeight: selected ? 600 : 500,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    color: selected ? 'primary.main' : 'text.secondary',
                    background: selected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.08),
                      color: 'primary.main',
                    },
                  }}
                >
                  {section.label}
                  {selected && (
                    <motion.div
                      layoutId="nav-underline"
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: '20%',
                        right: '20%',
                        height: 2,
                        borderRadius: 999,
                        background: theme.palette.primary.main,
                      }}
                    />
                  )}
                </Button>
              );
            })}
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            onClick={toggleTheme}
            color="inherit"
            sx={{
              background: alpha(theme.palette.text.primary, 0.08),
              borderRadius: '50%',
              flexShrink: 0,
              '&:hover': { background: alpha(theme.palette.text.primary, 0.15) },
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex' }}
              >
                {mode === 'dark' ? (
                  <LightModeOutlinedIcon fontSize="small" />
                ) : (
                  <DarkModeOutlinedIcon fontSize="small" />
                )}
              </motion.div>
            </AnimatePresence>
          </IconButton>
        </Tooltip>

        <NotificationPanel />

        <UserDropdown />
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
